import { Box, MantineTheme, useMantineTheme } from '@mantine/core';
import React from 'react';

type BadgeVariant = 'success' | 'error' | 'info';
type StyleVariant = 'outline' | 'solid' | 'subtle' | 'default';
type PresetColor = keyof MantineTheme['colors'];
type FontSize = 'sm' | 'md' | 'lg' | 'xl';

export interface BadgeProps {
  className?: string;
  children?: React.ReactNode;
  variant?: BadgeVariant;
  styleVariant?: StyleVariant;
  bg?: PresetColor | string;
  fontColor?: string;
  fz?: FontSize | number;
}

const getFontSizeFromTheme = (size: FontSize, theme: MantineTheme): number => {
  const fontSize = theme.fontSizes[size];
  return typeof fontSize === 'number' ? fontSize : parseInt(fontSize as string, 10);
};

const getVariantStyles = (variant: BadgeVariant, theme: MantineTheme): React.CSSProperties => {
  const variants = {
    success: {
      backgroundColor: theme.colors.green[7]
    },
    error: {
      backgroundColor: theme.colors.red[7]
    },
    info: {
      backgroundColor: theme.colors.blue[7]
    }
  };
  return variants[variant];
};

const getStyleVariantStyles = (
  styleVariant: StyleVariant,
  bg: string | undefined,
  fontColor: string | undefined,
  _theme: MantineTheme
): React.CSSProperties => {
  const color = bg && _theme.colors[bg as PresetColor] ? _theme.colors[bg as PresetColor][6] : bg;

  const variants = {
    outline: {
      color: fontColor || color,
      border: `1px solid ${color}`,
      backgroundColor: 'transparent'
    },
    subtle: {
      color: fontColor || color,
      backgroundColor: color ? `${color}1a` : undefined
    },
    solid: {
      color: fontColor || '#fff',
      backgroundColor: color
    },
    default: {
      color: fontColor || _theme.colors?.secondary[7],
      backgroundColor: _theme.colors?.gray[2]
    }
  };
  return variants[styleVariant];
};

const Badge = ({
  className,
  children,
  variant = 'info',
  styleVariant = 'solid',
  bg,
  fontColor,
  fz = 12
}: BadgeProps) => {
  const theme = useMantineTheme();

  const getFontSize = () => {
    if (typeof fz === 'string') {
      return getFontSizeFromTheme(fz as FontSize, theme);
    }
    return typeof fz === 'number' ? fz : theme.fontSizes.sm;
  };

  // Get base styles from variant
  const variantStyles = getVariantStyles(variant, theme);

  // If bg is provided, use styleVariant with that bg
  // If bg is not provided but variant is, use the variant's bg color for styleVariant
  const effectiveBg =
    bg || (variant && styleVariant === 'solid' ? variantStyles.backgroundColor : undefined);

  // Apply styleVariant styles, potentially overriding some variant styles
  const styleVariantStyles = getStyleVariantStyles(styleVariant, effectiveBg, fontColor, theme);

  // Merge styles, prioritizing explicit props
  const mergedStyles = {
    ...variantStyles,
    ...styleVariantStyles,
    // Ensure fontColor overrides everything if provided
    ...(fontColor ? { color: fontColor } : {})
  };

  return (
    <Box
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.25rem 0.5rem',
        borderRadius: theme.radius.xl,
        fontSize: getFontSize(),
        fontWeight: 700,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        letterSpacing: '0.05em',
        ...mergedStyles
      }}
    >
      {children}
    </Box>
  );
};

export default Badge;
